/**
 * surgery.service.ts
 *
 * Typed API wrappers for the Surgery worklist — the V5 port of the legacy
 * `consults_full_list-test.php` page. Uses the shared apiClient and returns
 * unwrapped envelope data, matching plans.service.ts.
 */
import { apiClient } from './apiClient';
import type { PaginatedResponse } from '@/types/api.types';

// ─── Domain types ─────────────────────────────────────────────────────────────

/**
 * How ready a case is, replacing the inline row colours legacy painted from
 * `$checklistOk` (green / yellow / red). The server decides; the client styles.
 *
 *  ready     — every checklist item satisfied
 *  attention — incomplete AND surgery is within two months (legacy's yellow)
 *  blocked   — the consult was not approved (legacy's red)
 *  open      — incomplete, but surgery is further out
 */
export type SurgeryRowStatus = 'ready' | 'attention' | 'blocked' | 'open';

export interface SurgeryListItem {
  consultRequestId: number;
  planId: number;
  patientId: number;
  planDisplayId: string | null;
  /** Null when the case carries no usable date — rendered blank, not as 0000. */
  procedureDate: string | null;
  /** IANA zone of the plan's facility (else the practice's); Procedure Date is shown in it. */
  facilityTimezone: string | null;
  patientName: string;
  procedures: string;
  doctorName: string;
  coordinatorName: string;
  labsReviewed: string;
  medicalClearance: string;
  due: number;
  consultStatus: string;
  note: string;
  lastUpdatedAt: string | null;
  /** Null when the case has never been updated. */
  lastUpdatedBy: string | null;
  rowStatus: SurgeryRowStatus;
}

/** The three search modes the legacy page actually offered. */
export type SurgerySearchType = 'name' | 'planId' | 'patientId';

/**
 * What the worklist sends, built by `buildSurgeryListParams`
 * (pages/surgery/surgeryShared.ts). Filters travel inside `filtersArray`, the
 * JSON string every list page sends (lib/filterRegistry `buildApiParams`).
 */
export interface SurgeryListParams {
  filtersArray: string;
  page?: number;
  perPage?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  searchType?: SurgerySearchType;
}

export interface SurgeryListMeta {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  sort: string | null;
  /**
   * True when the signed-in user is a doctor, in which case the server has
   * already narrowed the list to their own cases and the Doctor filter is
   * hidden — legacy did the same, enforcing it in the endpoint.
   */
  doctorLocked: boolean;
}

// ─── Calls ────────────────────────────────────────────────────────────────────

export function listSurgeryCases(
  params: SurgeryListParams,
): Promise<PaginatedResponse<SurgeryListItem> & { meta: SurgeryListMeta }> {
  return apiClient
    .get<PaginatedResponse<SurgeryListItem> & { meta: SurgeryListMeta }>('/surgery', { params })
    .then((r) => r.data);
}
